import { Button, Divider, Flex, Group, Modal, Select } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { ApiError, ApiErrorCode } from 'src/error/ApiError';
import { z } from 'zod';
import { GenericErrorModal } from '../error/GenericErrorModal';
import PageLoader from '../UI/loader/PageLoader';
import { useClientsContext } from './context/ClientsContext';
import { useAdvisors } from './hooks/useAdvisors';
import { useClients } from './hooks/useClients';
import { useEffect } from 'react';
import CreateClientForm from 'src/clients/CreateClientForm';
import ModalBackButton from 'src/UI/common/ModalBackButton';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { formatName } from 'src/utils/nameFormatting';

type Props = {
  opened: boolean;
  close: () => void;
  canGoBack?: boolean;
  previousStep?: () => void;
  emailAddress?: string;
  navigateAfterCreation?: boolean;
  onClientCreated?: () => void;
};

const formSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  emailAddress: z.string().email('Please enter a valid email address').nullable().optional(),
  advisorUserId: z.string().min(1, 'Please select an advisor'),
});

export default function CreateClientModal({
  opened,
  close,
  canGoBack,
  previousStep,
  emailAddress,
  navigateAfterCreation = true,
  onClientCreated = () => {},
}: Props) {
  const { currentUser, isSystemAdmin, isServiceAdmin } = useCurrentUser();
  const { advisors, isLoading: isAdvisorsLoading, refetch: refetchAdvisors } = useAdvisors();
  const { createClient, error, isLoading } = useClientsContext();
  const { clients } = useClients();
  const isDuplicateEmail = (emailAddress: string) => {
    return clients.some(
      client => client.emailAddress?.toLowerCase() === emailAddress.toLowerCase(),
    );
  };

  useEffect(() => {
    if (opened) {
      refetchAdvisors();
    }
  }, [opened, refetchAdvisors]);

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      firstName: '',
      lastName: '',
      emailAddress: emailAddress || '',
      advised: true,
      advisorUserId: '',
    },
    validate: values => {
      const zodErrors = zodResolver(formSchema)(values);
      if (!zodErrors.emailAddress && isDuplicateEmail(values.emailAddress || '')) {
        zodErrors.emailAddress = 'This email address is already in use.';
      }

      return zodErrors;
    },
    validateInputOnBlur: true,
  });

  useEffect(() => {
    if (currentUser?.id && !form.values.advisorUserId) {
      form.setFieldValue('advisorUserId', currentUser.id);
    }
  }, [currentUser?.id]);

  const onCreateClient = async (values: {
    emailAddress: string | null | undefined;
    firstName: string;
    lastName: string;
    advisorUserId: string;
    advised: boolean;
  }) => {
    try {
      await createClient(values, navigateAfterCreation);
      Mixpanel.track(MixpanelEvents.CLIENT_CREATED);
      close();
      form.reset();
      onClientCreated();
    } catch (e) {
      if (ApiError.isApiError(ApiErrorCode.DUPLICATE_EMAIL, e)) {
        form.setFieldError('emailAddress', e.message);
      }
      notifyAndLogError(
        'Failed to create client',
        'An error occurred while creating the client. Please try again.',
        e,
        { clientData: values },
      );
    }
  };

  if (error && !ApiError.isApiError(ApiErrorCode.DUPLICATE_EMAIL, error)) {
    return <GenericErrorModal opened={true} onClose={close} />;
  }

  return (
    <Modal opened={opened} onClose={close} title="Client details" centered>
      {previousStep && (
        <ModalBackButton previousStep={previousStep} canGoBack={Boolean(canGoBack)} />
      )}
      {isLoading || isAdvisorsLoading ? (
        <PageLoader absolute={false} />
      ) : (
        <form onSubmit={form.onSubmit(values => onCreateClient(values))}>
          <Flex direction="column" gap="md">
            <CreateClientForm form={form} />
            {/*Having typing issues with this select in the CreateClientForm so I extracted it and duplicated it*/}
            <Select
              label="Is this person a client?"
              placeholder="Please select"
              data={[
                { value: 'true', label: 'Yes, they are an advised client' },
                { value: 'false', label: 'No, they are a contact' },
              ]}
              onChange={value => form.setFieldValue('advised', value === 'true')}
              value={form.values.advised?.toString()}
            />

            {(isSystemAdmin || isServiceAdmin) && (
              <>
                <Divider my="md" color="black" />
                <Select
                  label="Advisor"
                  placeholder="Select advisor"
                  data={advisors.map(advisor => ({
                    value: advisor.id,
                    label: formatName(advisor.firstName, advisor.lastName),
                  }))}
                  {...form.getInputProps('advisorUserId')}
                />
              </>
            )}

            <Group justify="flex-end" mt="md">
              <Button type="submit" variant="filled-pill-black" disabled={!form.isValid()}>
                Add
              </Button>
            </Group>
          </Flex>
        </form>
      )}
    </Modal>
  );
}
